import { MusicResponseProps } from '@/types/musicProps'
import { useEffect, useRef, useState } from 'react'
import { IoHeart, IoPlay, IoReload, IoTrash } from 'react-icons/io5'
import { SceneDataProps } from '@/types/sceneProps'
import colors from 'tailwindcss/colors'
import { FormMusic } from '../FormMusic'

import { useModal } from '@/hooks/useModal'
import { useDispatch, useSelector } from 'react-redux'
import { ReduxProps } from '@/storage'

import {
  TrackListRemoteProps,
  handleAppendTrackListRemote,
  handleTrackListRemote,
} from '@/storage/modules/trackListRemote/reducer'
import { useToast } from '@/hooks/useToast'
import { api } from '@/services/api'
import { CatalogStatusBadge } from '../CatalogStatusBadge'
import { EmptyState } from '../EmptyState'
import {
  EMPTY_PAGINATION_META,
  type PaginatedResponseProps,
  type PaginationMetaProps,
  normalizePaginationMeta,
} from '@/types/paginationProps'
import { SCENES_UPDATED_EVENT } from '@/services/scenesEvents'
import { fetchScenesOptions } from '@/services/scenes'

interface MusicMetricProps {
  icon: JSX.Element
  label: string
  value: number
}

function MusicMetric({ icon, label, value }: MusicMetricProps) {
  return (
    <div className="rounded-xl bg-gray-200/70 px-3 py-2 text-left">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {icon}
        <span>{label}</span>
      </div>
      <p className="mt-2 text-lg font-bold text-gray-700">{value}</p>
    </div>
  )
}

export function Musics() {
  const { trackListRemote, currentPage, lastPage, totalItems } = useSelector<
    ReduxProps,
    TrackListRemoteProps
  >((state) => state.trackListRemote)

  const [titleFilter, setTitleFilter] = useState('')
  const [sceneOptions, setSceneOptions] = useState<SceneDataProps[]>([])
  const [selectedSceneSlug, setSelectedSceneSlug] = useState('')
  const [musicsFiltered, setMusicFilteres] = useState<MusicResponseProps[]>([])
  const [filteredMeta, setFilteredMeta] = useState<PaginationMetaProps>(
    EMPTY_PAGINATION_META,
  )
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isLoadingScenes, setIsLoadingScenes] = useState(false)
  const isLoadingMoreRef = useRef(false)
  const paginationMetaRef = useRef<PaginationMetaProps>(EMPTY_PAGINATION_META)
  const isFilteringRef = useRef(false)
  const isSearchingRef = useRef(false)
  const titleFilterRef = useRef('')
  const selectedSceneSlugRef = useRef('')
  const canLoadMoreRef = useRef(false)

  const dispatch = useDispatch()

  const { openModal, closeModal } = useModal()
  const { showToast } = useToast()

  const hasTitleFilter = titleFilter.trim().length >= 3
  const isFiltering = hasTitleFilter || Boolean(selectedSceneSlug)
  const musicsToRender = isFiltering ? musicsFiltered : trackListRemote
  const paginationMeta = isFiltering
    ? filteredMeta
    : {
        currentPage,
        lastPage,
        totalItems,
      }

  const canLoadMore =
    paginationMeta.lastPage > 0 &&
    paginationMeta.currentPage < paginationMeta.lastPage

  const handleFetchMusicPage = async (
    page: number,
    filter?: string,
    scene?: string,
  ) => {
    const response = await api.get<PaginatedResponseProps<MusicResponseProps>>(
      '/musics',
      {
        params: {
          page,
          ...(filter ? { title: filter } : {}),
          ...(scene ? { scene } : {}),
        },
      },
    )

    const pagination = normalizePaginationMeta(
      response.data.meta,
      response.data.data.length,
    )

    return {
      musics: response.data.data,
      pagination,
    }
  }

  const handleFilterMusic = async (filter: string) => {
    setTitleFilter(filter)
    const currentScene = selectedSceneSlugRef.current

    if (filter.trim().length < 3 && !currentScene) {
      setMusicFilteres([])
      setFilteredMeta(EMPTY_PAGINATION_META)
      return
    }

    try {
      setIsSearching(true)
      const response = await handleFetchMusicPage(
        1,
        filter.trim().length >= 3 ? filter.trim() : undefined,
        currentScene || undefined,
      )
      setMusicFilteres(response.musics)
      setFilteredMeta(response.pagination)
    } catch (error) {
      showToast('Error fetching musics', {
        type: 'error',
        theme: 'light',
      })
    } finally {
      setIsSearching(false)
    }
  }

  const handleSelectScene = async (sceneSlug: string) => {
    setSelectedSceneSlug(sceneSlug)
    const currentFilter = titleFilterRef.current

    if (!sceneSlug && currentFilter.length < 3) {
      setMusicFilteres([])
      setFilteredMeta(EMPTY_PAGINATION_META)
      return
    }

    try {
      setIsSearching(true)
      const response = await handleFetchMusicPage(
        1,
        currentFilter.length >= 3 ? currentFilter : undefined,
        sceneSlug || undefined,
      )
      setMusicFilteres(response.musics)
      setFilteredMeta(response.pagination)
    } catch (error) {
      showToast('Error filtering musics by scene', {
        type: 'error',
        theme: 'light',
      })
    } finally {
      setIsSearching(false)
    }
  }

  const handleRefreshMusics = async () => {
    const currentFilter = titleFilterRef.current
    const currentScene = selectedSceneSlugRef.current

    try {
      setIsRefreshing(true)

      if (currentFilter.length >= 3 || currentScene) {
        const response = await handleFetchMusicPage(
          1,
          currentFilter.length >= 3 ? currentFilter : undefined,
          currentScene || undefined,
        )
        setMusicFilteres(response.musics)
        setFilteredMeta(response.pagination)
        return
      }

      const response = await handleFetchMusicPage(1)

      dispatch(
        handleTrackListRemote({
          trackListRemote: response.musics,
          currentPage: response.pagination.currentPage,
          lastPage: response.pagination.lastPage,
          totalItems: response.pagination.totalItems,
        }),
      )
    } catch (error) {
      showToast('Error refreshing musics', {
        type: 'error',
        theme: 'light',
      })
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleRemoveMusic = async (music: MusicResponseProps) => {
    if (music.id) {
      try {
        await api.delete(`/musics/${music.id}`)

        showToast('Music removed successfully', {
          type: 'success',
          theme: 'light',
        })

        closeModal()

        if (isFiltering) {
          setMusicFilteres((prev) =>
            prev.filter((item) => item.id !== music.id),
          )
          setFilteredMeta((prev) => ({
            ...prev,
            totalItems: Math.max(0, prev.totalItems - 1),
          }))
          return
        }

        dispatch(
          handleTrackListRemote({
            trackListRemote: trackListRemote.filter(
              (item) => item.id !== music.id,
            ),
            currentPage,
            lastPage,
            totalItems: Math.max(0, totalItems - 1),
          }),
        )
      } catch (error) {
        showToast(`Error removing music`, {
          type: 'error',
          theme: 'light',
        })
      }
    }
  }

  useEffect(() => {
    if (!isFiltering) {
      setMusicFilteres([])
      setFilteredMeta(EMPTY_PAGINATION_META)
    }
  }, [isFiltering, trackListRemote])

  useEffect(() => {
    let isMounted = true

    const loadScenes = async () => {
      try {
        setIsLoadingScenes(true)
        const scenes = await fetchScenesOptions()

        if (!isMounted) return

        setSceneOptions(scenes)
      } catch (error) {
        if (!isMounted) return

        showToast('Error fetching scenes', {
          type: 'error',
          theme: 'light',
        })
      } finally {
        if (isMounted) {
          setIsLoadingScenes(false)
        }
      }
    }

    loadScenes()

    return () => {
      isMounted = false
    }
  }, [showToast])

  useEffect(() => {
    paginationMetaRef.current = isFiltering
      ? filteredMeta
      : {
          currentPage,
          lastPage,
          totalItems,
        }
    isFilteringRef.current = isFiltering
    isSearchingRef.current = isSearching
    titleFilterRef.current = titleFilter.trim()
    selectedSceneSlugRef.current = selectedSceneSlug
    canLoadMoreRef.current = canLoadMore
  }, [
    canLoadMore,
    currentPage,
    filteredMeta,
    isFiltering,
    isSearching,
    lastPage,
    selectedSceneSlug,
    titleFilter,
    totalItems,
  ])

  useEffect(() => {
    const loadMoreMusics = async () => {
      const nextPage = paginationMetaRef.current.currentPage + 1

      if (
        !canLoadMoreRef.current ||
        nextPage <= 0 ||
        isSearchingRef.current ||
        isLoadingMoreRef.current
      ) {
        return
      }

      try {
        isLoadingMoreRef.current = true
        setIsLoadingMore(true)

        const response = await handleFetchMusicPage(
          nextPage,
          isFilteringRef.current ? titleFilterRef.current : undefined,
          selectedSceneSlugRef.current || undefined,
        )
        paginationMetaRef.current = response.pagination
        canLoadMoreRef.current =
          response.pagination.lastPage > 0 &&
          response.pagination.currentPage < response.pagination.lastPage

        if (isFilteringRef.current) {
          setMusicFilteres((prev) => [...prev, ...response.musics])
          setFilteredMeta(response.pagination)
          return
        }

        dispatch(
          handleAppendTrackListRemote({
            trackListRemote: response.musics,
            currentPage: response.pagination.currentPage,
            lastPage: response.pagination.lastPage,
            totalItems: response.pagination.totalItems,
          }),
        )
      } catch (error) {
        showToast('Error fetching more musics', {
          type: 'error',
          theme: 'light',
        })
      } finally {
        isLoadingMoreRef.current = false
        setIsLoadingMore(false)
      }
    }

    const handleScroll = () => {
      if (
        !canLoadMoreRef.current ||
        isSearchingRef.current ||
        isLoadingMoreRef.current
      ) {
        return
      }

      const viewportHeight = window.innerHeight
      const scrollTop = window.scrollY || document.documentElement.scrollTop
      const fullHeight = document.documentElement.scrollHeight

      if (!fullHeight) {
        return
      }

      const scrollProgress = (scrollTop + viewportHeight) / fullHeight

      if (scrollProgress >= 0.8) {
        loadMoreMusics()
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [dispatch, showToast])

  useEffect(() => {
    const handleScenesUpdated = async () => {
      try {
        const scenes = await fetchScenesOptions()
        setSceneOptions(scenes)

        const currentSceneStillExists = scenes.some(
          (scene) => scene.slug === selectedSceneSlugRef.current,
        )

        if (!currentSceneStillExists && selectedSceneSlugRef.current) {
          setSelectedSceneSlug('')

          if (titleFilterRef.current.length >= 3) {
            const response = await handleFetchMusicPage(
              1,
              titleFilterRef.current,
            )
            setMusicFilteres(response.musics)
            setFilteredMeta(response.pagination)
          } else {
            setMusicFilteres([])
            setFilteredMeta(EMPTY_PAGINATION_META)
          }
        }
      } catch (error) {
        showToast('Error refreshing scenes', {
          type: 'error',
          theme: 'light',
        })
      }
    }

    window.addEventListener(SCENES_UPDATED_EVENT, handleScenesUpdated)

    return () => {
      window.removeEventListener(SCENES_UPDATED_EVENT, handleScenesUpdated)
    }
  }, [showToast])

  return (
    <div>
      <div className="h-[1px] bg-gray-300/50 my-7" />
      <div className="mt-8">
        <h4 className="font-bold text-sm my-3">Search by title</h4>
        <div className="flex max-w-[28rem] items-center gap-2">
          <input
            placeholder="Type a Song"
            type="text"
            onChange={(e) => {
              handleFilterMusic(e.currentTarget.value)
            }}
            className={
              'w-full bg-white border border-gray-300 rounded px-4 py-2 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 text-sm font-normal data-[is-error=true]:border-red-600'
            }
          />
          <button
            type="button"
            title="Reload musics"
            onClick={handleRefreshMusics}
            disabled={isSearching || isRefreshing}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded border border-gray-300 bg-white text-purple-600 transition hover:border-purple-300 hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <IoReload
              size={18}
              className={isRefreshing ? 'animate-spin' : undefined}
            />
          </button>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => handleSelectScene('')}
            className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
              !selectedSceneSlug
                ? 'border-purple-600 bg-purple-600 text-white'
                : 'border-gray-300 bg-white text-gray-600 hover:border-purple-300 hover:text-purple-600'
            }`}
          >
            All scenes
          </button>

          {sceneOptions.map((scene) => (
            <button
              key={scene.id}
              type="button"
              onClick={() => handleSelectScene(scene.slug)}
              className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
                selectedSceneSlug === scene.slug
                  ? 'border-purple-600 bg-purple-600 text-white'
                  : 'border-gray-300 bg-white text-gray-600 hover:border-purple-300 hover:text-purple-600'
              }`}
            >
              {scene.title}
            </button>
          ))}

          {isLoadingScenes && (
            <span className="px-1 py-1.5 text-sm text-gray-500">
              Loading scenes...
            </span>
          )}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between gap-3 md:flex-col md:items-start">
        <p className="text-sm text-gray-500">
          Showing {musicsToRender.length} of {paginationMeta.totalItems} musics
        </p>

        {isFiltering && (
          <p className="text-sm text-gray-500">
            {isSearching
              ? 'Searching...'
              : [
                  hasTitleFilter ? `title "${titleFilter}"` : null,
                  selectedSceneSlug ? `scene "${selectedSceneSlug}"` : null,
                ]
                  .filter(Boolean)
                  .join(' + ')}
          </p>
        )}
      </div>

      {musicsToRender.map((music) => (
        <div
          key={music.id}
          className="relative mt-6 w-full rounded-2xl border bg-white p-6 transition-colors hover:border-gray-300 base:p-5 md:p-4"
        >
          <button
            title="Remove"
            className="absolute -right-1.5 -top-1.5 z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-red-600 bg-red-600 shadow-sm transition hover:bg-red-700"
            onClick={() => {
              openModal({
                textConfirm: 'Delete',
                description: 'Do you really want to delete the music?',
                title: 'Attention',

                confirm() {
                  handleRemoveMusic(music)
                },
              })
            }}
          >
            <IoTrash size={14} color={colors.white} />
          </button>

          <div className="flex items-start justify-between gap-6 base:flex-col">
            <button
              className="flex min-w-0 flex-1 items-start gap-4 text-left md:flex-col md:gap-3"
              title="Open music"
              onClick={() => {
                openModal({
                  children: <FormMusic musicId={music.id} />,
                })
              }}
            >
              <div className="h-16 w-16 shrink-0 overflow-hidden rounded-full bg-gray-700">
                <img
                  src={music.artwork}
                  alt="artwork track music"
                  className="h-full w-full object-cover"
                />
              </div>

              <div className="min-w-0 flex-1">
                <p className="truncate font-bold text-purple-600">
                  {music.title}
                </p>
                <p className="mt-1 truncate text-sm text-gray-500">
                  {typeof music.album === 'string'
                    ? music.album || 'No album'
                    : music.album?.title || 'No album'}
                </p>

                <div className="mt-3 grid grid-cols-1 gap-2 justify-items-start">
                  <CatalogStatusBadge
                    label="Discover"
                    value={music.discoveryStatus}
                  />
                  <CatalogStatusBadge
                    label="Visual"
                    value={music.visualAssetStatus}
                  />
                  <CatalogStatusBadge
                    label="Playback"
                    value={music.playbackStatus}
                  />
                </div>
              </div>
            </button>

            <div className="w-full max-w-[360px] shrink-0 base:max-w-none">
              <div>
                <div className="rounded-xl bg-gray-100 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Artists
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {music.artists.map((artist) => (
                      <span
                        key={artist.id}
                        className="rounded-full bg-white px-3 py-1 text-sm font-semibold text-gray-700"
                      >
                        {artist.title}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-1 gap-2">
                  <MusicMetric
                    icon={<IoHeart size={16} color={colors.red[600]} />}
                    label="Likes"
                    value={music.like ?? 0}
                  />
                  <MusicMetric
                    icon={<IoPlay size={16} color={colors.blue[600]} />}
                    label="Streams"
                    value={music.view ?? 0}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      {musicsToRender.length === 0 && !isSearching && (
        <EmptyState
          title={isFiltering ? 'No musics found' : 'No musics yet'}
          description={
            isFiltering
              ? 'Try another search term to find an existing music.'
              : 'Create the first music to start managing the catalog.'
          }
        />
      )}

      {isLoadingMore && (
        <div className="mt-8 flex items-center justify-center gap-3 text-sm text-gray-500">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-purple-200 border-t-purple-600" />
          <p>Loading more musics...</p>
        </div>
      )}
    </div>
  )
}
